import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMatch, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import PaymentTableRow from "./components/PaymentRow";
import PaymentHeaderSection from "./components/PaymentTableHeaderSection";
import { AddEdit as AddEditPaymentInterface, Type } from "../../../interfaces/payment.interface";
import PaymentService from "../../../services/payment.service";
import Header from "./components/Header";
import PrimaryDetails from "./components/PrimaryDetails";
import Tabs from "./components/Tabs";
import DetailsTab from "./components/DetailsTab";
import DetailsTabButtons from "./components/DetailsTabButtons";
import InvoiceTab from "./components/InvoiceTab";
import PaymentsTab from "./components/PaymentsTab";
import ReservationRow from "./components/ReservationRow";
import TransactionRow from "./components/TransactionRow";
import { Edit as EditDetailsInterface, EditApi } from "../../../interfaces/invoice.interface";
import { HeadCellType, TableContextProvider, Validation } from "../../../interfaces/common";
import useFetch from "../../../hooks/useFetch";
import InvoiceService from "../../../services/invoice.service";
import { STATUS_CODE } from "../../../utils/apiConstant";
import { Modules, SortOrder, RouteParamsTypes, NonAuthRoutes } from "../../../enums";
import { buildHeadCells, formatDate, extractDateFromDateTimeBySpace } from "../../../utils/methods";
import { APP_SUCCESS_MESSAGES, GENERIC_LOOKUP_KEYS, HEAD_CELL_SORT_MAPPING, INVOICE_STATUS, VALIDATION_FIELD_MAPPING, DEFAULTS, TRANSACTION_STATUS, PAYMENT_TYPES } from "../../../utils/appConstant";
import ReservationService from "../../../services/reservation.service";
import TransactionService from "../../../services/transaction.service";
import UserService from "../../../services/user.service";
import { validateEditDetailsTabForm, validateAddEditPaymentForm } from "./validations";
import CommonService from "../../../services/common.service";
import PayoutService from "../../../services/payout.service";
import Toast from '../../../components/Toast';
import useSorting from "../../../hooks/useSorting";
import Cards from "./components/Cards";
import PayoutAmtModalContent from "../../../components/PayoutAmtModalContent";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import useUnsavedChangesWarning from "../../../hooks/useUnsavedChangesWarning";
import ConfirmationFormRouting from "../../../components/ConfirmationFormRouting";

const initialDetailsFormData: EditDetailsInterface = {
  property: '',
  propertyId: '',
  propertyImg: '',
  invoiceNo: '',
  status: '',
  statusName: '',
  month: '',
  invoiceDate: '',
  invoiceDateApi: '',
  bookingTypeName: '',
  commission: '',
  customer: '',
  createdBy: '',
  createdDate: '',
  updatedBy: '',
  updatedDate: '',
  sentBy: '',
  sentDate: '',
  completedBy: '',
  completedDate: '',
  dueDate: '',
  dueDateApi: '',
  notes: '',
  reviewed: false,
  isVerified: false,
  reviewedBy: "",
  includeNotes: false,
  selectedReservations: [],
  selectedTransactions: []
};

const initialPaymentsFormData: AddEditPaymentInterface = {
  id: '',
  date: formatDate(new Date()),
  amount: '',
  method: '',
  account: '',
  notes: '',
  type: DEFAULTS.paymentType as Type
};

const initialValidation: Validation = { validate: true, fields: [] };
const initialPaymentValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  reviewedBy: '',
  account: '',
  method: ''
};

const EditPreviewPayment: React.FC = () => {
  const { id } = useParams();
  const view = !!useMatch(Modules.invoices + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.invoices + RouteParamsTypes.edit + '/:id');
  const location = useLocation()
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formData, setFormData] = useState<EditDetailsInterface>(initialDetailsFormData);
  const [pdfData, setPdfData] = useState<string>('');
  const [paymentFormData, setPaymentFormData] = useState<AddEditPaymentInterface>(initialPaymentsFormData);
  const [tab, setTab] = useState<'details' | 'invoice' | 'payments'>('details');
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [markAsCompleteLoading, setMarkAsCompleteLoading] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [detailsCounter, setDetailsCounter] = useState<number>(0);
  const [previewCounter, setPreviewCounter] = useState<number>(0);
  const [paymentCounter, setPaymentCounter] = useState<number>(0);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [paymentValidation, setPaymentValidation] = useState<Validation>(initialPaymentValidation);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [open, setOpen] = useState<{
    addEditPayment: boolean;
    deletePayment: string;
    invoiceComplete: boolean;
    sendEmail: boolean;
    payoutAmt: boolean;
  }>({
    addEditPayment: false,
    deletePayment: '',
    invoiceComplete: false,
    sendEmail: false,
    payoutAmt: false
  })

  const [payoutAmtItem, setPayoutAmtItem] = useState<any>(null);
  const [transactionOpen, setTransactionOpen] = useState<{
    paramId: string;
    mode: "view" | "edit";
  }>({
    paramId: '',
    mode: 'view'
  })
  const [reservationOpen, setReservationOpen] = useState<{
    paramId: string;
    mode: "view" | "edit";
  }>({
    paramId: '',
    mode: 'view'
  })
  const [selectAll, setSelectAll] = useState<{
    reservations: boolean;
    transactions: boolean;
  }>({
    reservations: false,
    transactions: false
  });

  const [shouldBlock, setShouldBlock] = useState(!view);

  // Unsaved changes warning
  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChangesWarning({
    when: shouldBlock,
  });

  useEffect(() => {
    setTab(location?.state?.tab ?? 'details');
    setShouldBlock(!view);
  }, [location.state, location.key, view]);

  const paymentHeadCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.date,
    VALIDATION_FIELD_MAPPING.amount,
    VALIDATION_FIELD_MAPPING.type,
    VALIDATION_FIELD_MAPPING.method,
    VALIDATION_FIELD_MAPPING.account,
    VALIDATION_FIELD_MAPPING.notes,
    VALIDATION_FIELD_MAPPING.actions,
  ], 'invoicePaymentList');

  const reservationHeadCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.checkInDetails,
    VALIDATION_FIELD_MAPPING.guest,
    VALIDATION_FIELD_MAPPING.reservationStatus,
    VALIDATION_FIELD_MAPPING.hostPayout,
    VALIDATION_FIELD_MAPPING.source,
    VALIDATION_FIELD_MAPPING.status,
    VALIDATION_FIELD_MAPPING.lastSync,
  ], 'invoiceReservationList');

  const transactionHeadCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.actions,
    VALIDATION_FIELD_MAPPING.transactionDate,
    VALIDATION_FIELD_MAPPING.debitCredit,
    VALIDATION_FIELD_MAPPING.transactionType,
    VALIDATION_FIELD_MAPPING.transactionDetails,
    VALIDATION_FIELD_MAPPING.actualAmount,
    VALIDATION_FIELD_MAPPING.amount,
    VALIDATION_FIELD_MAPPING.status,
    VALIDATION_FIELD_MAPPING.lastUpdated,
  ], 'invoiceTransactionList');

  const {
    order: reservationOrder, orderBy: reservationOrderBy, setOrder: reservationSetOrder, setOrderBy: reservationSetOrderBy
  } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: VALIDATION_FIELD_MAPPING.checkInDetails });

  const {
    order: transactionOrder, orderBy: transactionOrderBy, setOrder: transactionSetOrder, setOrderBy: transactionSetOrderBy
  } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: VALIDATION_FIELD_MAPPING.status });

  const {
    order: paymentOrder, orderBy: paymentOrderBy, setOrder: paymentSetOrder, setOrderBy: paymentSetOrderBy
  } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: VALIDATION_FIELD_MAPPING.date });

  const { data: paymentKpis } = useFetch({
    service: PaymentService,
    func: "kpis",
    payload: id,
    dependency: [id, paymentCounter],
    skip: !id,
  });

  const { data: payments, loading: paymentsLoading, total: paymentsTotal } = useFetch({
    service: PaymentService,
    func: "list",
    payload: {
      invoiceId: id,
      page: 1,
      perPage: 100,
      orderBy: HEAD_CELL_SORT_MAPPING.invoicePaymentList[paymentOrderBy] || paymentOrderBy,
      order: paymentOrder
    },
    dependency: [id, paymentCounter, paymentOrder, paymentOrderBy],
    skip: !id,
  });

  const { data: reservations, loading: reservationLoading } = useFetch({
    service: ReservationService,
    func: 'list',
    payload: {
      invoiceId: id,
      orderBy: HEAD_CELL_SORT_MAPPING.invoiceReservationList[reservationOrderBy] || reservationOrderBy,
      order: reservationOrder,
      perPage: 1000,
      isActive: 1
    },
    dependency: [id, detailsCounter, reservationOrder, reservationOrderBy],
    onSuccess: (response: any) => {
      const tempSelectedReservations: any[] = [];
      (response?.records ?? []).map((item: any) => {
        if (item?.is_selected) tempSelectedReservations.push(item.id)
      })
      setFormData(prev => {
        return {
          ...prev,
          selectedReservations: tempSelectedReservations
        }
      })

      if (response?.records && response.records?.length && response.records.length === tempSelectedReservations.length) {
        setSelectAll(prev => {
          return {
            ...prev,
            reservations: true
          }
        })
      }
    }
  });

  const [payoutAmtData, setPayoutAmtData] = useState<any>(null);

  const { loading: payoutAmtLoading } = useFetch({
    service: ReservationService,
    func: 'payoutDetails',
    payload: payoutAmtItem?.id,
    dependency: [payoutAmtItem],
    skip: !payoutAmtItem,
    onSuccess: (response: any) => {
      setPayoutAmtData({
        property_name: response.property_name,
        guest_name: response.guest_name,
        date: response.date,
        reservation_status_name: response.status,
        reservation_code: payoutAmtItem?.reservation_code,
        payout_breakdown: response.transactions,
        payout_breakdown_total_payout: response.total_payout,
        verification_status_name: response.verification_status_name,
        notes: response.notes,
      });
    }
  });

  const { data: transactions, loading: transactionLoading } = useFetch({
    service: TransactionService,
    func: 'list',
    payload: {
      invoiceId: id,
      orderBy: HEAD_CELL_SORT_MAPPING.invoiceTransactionList[transactionOrderBy] || transactionOrderBy,
      order: transactionOrder,
      perPage: 1000
    },
    dependency: [id, detailsCounter, transactionOrderBy, transactionOrder],
    onSuccess: (response: any) => {
      const tempSelectedTransactions: any[] = [];
      (response?.records ?? []).map((item: any) => {
        if (item?.is_selected || selectAll.transactions) tempSelectedTransactions.push(item.id)
      })
      setFormData(prev => {
        return {
          ...prev,
          selectedTransactions: tempSelectedTransactions
        }
      })

      if (response?.records && response.records?.length && response.records.length === tempSelectedTransactions.length) {
        setSelectAll(prev => {
          return {
            ...prev,
            transactions: true
          }
        })
      }
    }
  });

  const { data: paymentAccounts } = useFetch({
    service: PaymentService,
    func: "getAccountLookup",
    payload: { searchText: dropdownSearchData.account },
    dependency: [dropdownSearchData.account],
    skip: !id,
  });

  const { data: allPaymentAccounts } = useFetch({
    service: PaymentService,
    func: "getAccountLookup",
    payload: { searchText: "" },
    dependency: [],
    skip: !id,
  });

  const { data: paymentMethods } = useFetch({
    service: CommonService,
    func: "genericLookup",
    payload: { key: GENERIC_LOOKUP_KEYS.paymentMethod, searchText: dropdownSearchData.method },
    dependency: [dropdownSearchData.method],
    skip: !id,
  });

  const { data: allPaymentMethods } = useFetch({
    service: CommonService,
    func: "genericLookup",
    payload: { key: GENERIC_LOOKUP_KEYS.paymentMethod, searchText: "" },
    dependency: [],
    skip: !id,
  });

  const { data: invoicePaymentTypes } = useFetch({
    service: CommonService,
    func: "genericLookup",
    payload: { key: GENERIC_LOOKUP_KEYS.paymentType, searchText: "" },
    dependency: [],
    skip: !id,
  });

  const { data: allReviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
    skip: !id,
  });

  const { data: reviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.reviewedBy, is_active: '1', include: 'properties', include_id: formData.propertyId },
    dependency: [dropdownSearchData.reviewedBy, formData.propertyId],
    skip: !id,
  });

  const { loading: previewLoading } = useFetch({
    service: InvoiceService,
    func: "preview",
    payload: id,
    dependency: [id, previewCounter],
    skip: !id,
    onSuccess: (response: any) => {
      setPdfData(response?.pdf_base64 || '');
    }
  });

  const { loading: getContentLoading } = useFetch({
    service: InvoiceService,
    func: "get",
    payload: id,
    dependency: [id, detailsCounter],
    skip: !id,
    onSuccess: (response: any) => {
      if (response.status === INVOICE_STATUS.completed && window.location.pathname.includes('/edit/')) {
        navigate(Modules.error + NonAuthRoutes.forbidden);
      }

      setFormData(prev => {
        return {
          ...prev,
          property: response?.property?.name,
          propertyId: response?.property?.id,
          propertyImg: response?.property?.picture_url,
          invoiceNo: response.invoice_no,
          status: response.status,
          statusName: response.status_name,
          month: response.month_name && response.year ? `${response.month_name}-${response.year}` : '-',
          invoiceDate: response.formatted_invoice_date ? formatDate(new Date(response.formatted_invoice_date)) : '',
          invoiceDateApi: response.formatted_invoice_date || '',
          bookingTypeName: response.booking_type_name || '',
          customer: response?.customer?.customer_name,
          createdBy: response?.creator?.name,
          createdDate: extractDateFromDateTimeBySpace(response.created_at),
          updatedBy: response?.updater?.name,
          updatedDate: extractDateFromDateTimeBySpace(response.updated_at),
          sentBy: response.last_sent_by_name,
          sentDate: extractDateFromDateTimeBySpace(response.last_sent_at),
          commission: response.commission,
          completedBy: response?.completedBy?.name,
          completedDate: extractDateFromDateTimeBySpace(response.completed_at),
          dueDate: response.formatted_due_date ? formatDate(new Date(response.formatted_due_date)) : '',
          dueDateApi: response.formatted_due_date || '',
          notes: response.notes,
          reviewed: response.reviewed,
          isVerified: response.is_verified,
          reviewedBy: response.reviewed_by,
          includeNotes: response.include_notes || false,
        }
      })
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.invoices);
    }
  });

  const invoicePaymentTypeOptions = (invoicePaymentTypes as any)?.map((type: any) => ({
    label: type.name,
    value: type.value
  })) || [
      { label: "Received", value: "received" },
      { label: "Paid", value: "paid" },
      { label: "Forward", value: "carry_forward" }
    ];

  const handlePaymentInputChange = useCallback((event: any, name: string) => {
    setPaymentFormData(prev => {
      const updated = { ...prev, [name]: event.target.value };
      if (name === VALIDATION_FIELD_MAPPING.type && event.target.value === 'carry_forward') {
        updated.method = '';
        updated.account = '';
      }
      return updated;
    });
  }, [setPaymentFormData]);

  const handleInputChange = useCallback((event: any, name: string, type?: string, id?: string) => {
    if (name === VALIDATION_FIELD_MAPPING.actions) {
      if (type === 'reservations') {
        let isAllSelected = false;
        setFormData(prev => {
          let tempSelectedReservations = prev.selectedReservations;
          if (tempSelectedReservations.includes(id)) {
            tempSelectedReservations = tempSelectedReservations.filter(selected => selected !== id);
          } else {
            tempSelectedReservations = [...tempSelectedReservations, id];
          }

          reservations.length === tempSelectedReservations.length && (isAllSelected = true)

          return {
            ...prev,
            selectedReservations: tempSelectedReservations
          };
        });
        setSelectAll(prev => {
          return {
            ...prev,
            reservations: isAllSelected
          }
        })
      }

      if (type === 'transactions') {
        let isAllSelected = false;
        setFormData(prev => {
          let tempSelectedTransactions = prev.selectedTransactions;
          if (tempSelectedTransactions.includes(id)) {
            tempSelectedTransactions = tempSelectedTransactions.filter(selected => selected !== id);
          } else {
            tempSelectedTransactions = [...tempSelectedTransactions, id];
          }

          transactions.length === tempSelectedTransactions.length && (isAllSelected = true)

          return {
            ...prev,
            selectedTransactions: tempSelectedTransactions
          };
        });
        setSelectAll(prev => {
          return {
            ...prev,
            transactions: isAllSelected
          }
        })
      }
    } else {
      let value: any;

      if (name === VALIDATION_FIELD_MAPPING.reviewed || name === VALIDATION_FIELD_MAPPING.includeNotes || name === VALIDATION_FIELD_MAPPING.isVerified) {
        value = !!event.target.checked;
      } else {
        value = event.target.value;
      }

      setFormData((prev) => {
        const newData = {
          ...prev,
          [name]: value
        };

        if (name === VALIDATION_FIELD_MAPPING.reviewed && !value) {
          newData.isVerified = false;
        }

        return newData;
      });
    }
  }, [setFormData, reservations, transactions]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const handlePaymentInputBlur = useCallback((event: any, name: string) => {
    let newValidation = validateAddEditPaymentForm({
      ...paymentFormData,
      [name]: event.target.value.trim()
    }, allPaymentAccounts, name);
    setPaymentValidation(newValidation);
  }, [paymentFormData, allPaymentAccounts]);

  const handleInputBlur = useCallback((event: any, name: string) => {
    let newValidation = validateEditDetailsTabForm({
      ...formData,
      [name]: event.target.value.trim(),
    }, name);
    setValidation(prev => {
      const filteredFields = prev.fields.filter(f => f.field !== name);
      const mergedFields = [...filteredFields, ...newValidation.fields];

      return {
        validate: mergedFields.length === 0,
        fields: mergedFields,
      };
    });
  }, [formData]);

  const handleTabChange = useCallback((e: any, value: 'details' | 'invoice' | 'payments') => {
    if (value === 'payments') setPaymentCounter(prev => prev + 1);
    if (value === 'invoice') setPreviewCounter(prev => prev + 1);
    if (value === 'details') {
      setDetailsCounter(prev => prev + 1);
      setSelectAll({
        reservations: false,
        transactions: false
      })
    }

    setTab(value);
  }, []);

  const handleAddPaymentClick = () => {
    const amountDueRaw = (paymentKpis as any)?.outstanding_amount;
    const amountDue = parseFloat(amountDueRaw?.replace(/,/g, '') || '0');
    let paymentType: Type = PAYMENT_TYPES.received as Type;
    let amount = '0.00';

    if (amountDue < 0) {
      amount = Math.abs(amountDue).toFixed(2);
      paymentType = PAYMENT_TYPES.paid as Type;
    } else if (amountDue > 0) {
      amount = amountDue.toFixed(2);
      paymentType = PAYMENT_TYPES.received as Type;
    } else {
      amount = '0.00';
      paymentType = PAYMENT_TYPES.paid as Type;
    }

    const defaultMethod = (allPaymentMethods || []).find((item: any) =>
      item.id === DEFAULTS.paymentMethod
    );

    const defaultAccount = (allPaymentAccounts || []).find((item: any) =>
      item.code === DEFAULTS.paymentAccount
    );

    setPaymentFormData({
      ...initialPaymentsFormData,
      amount: '',
      type: paymentType,
      method: defaultMethod?.id || '',
      account: defaultAccount?.id || ''
    });

    setOpen(prev => {
      return {
        ...prev,
        addEditPayment: true
      }
    });
  };

  const handleEditPaymentClick = (data: any) => {
    setPaymentFormData({
      id: data.id,
      date: formatDate(new Date(data.payment_date)),
      amount: Number(data.payment_amount ? data.payment_amount.replace(/,/g, "") : 0).toFixed(2),
      method: data.payment_method,
      account: data.account_id,
      notes: data.note,
      type: data.payment_type
    });

    setOpen(prev => {
      return {
        ...prev,
        addEditPayment: true
      }
    });
  };

  const handleDeletePaymentClick = (paymentId: string) => {
    setOpen(prev => {
      return {
        ...prev,
        deletePayment: paymentId
      }
    });
  };

  const handleMarkAsCompleteClick = () => {
    setOpen(prev => {
      return {
        ...prev,
        invoiceComplete: true
      }
    });
  };

  const handleSendEmailClick = () => {
    setOpen(prev => {
      return {
        ...prev,
        sendEmail: true
      }
    });
  };

  const handleCancel = () => {
    setShouldBlock(false);
    setTimeout(() => {
      navigate(Modules.invoices);
    }, 0);
  };

  const handlePaymentAddEditClose = () => {
    setPaymentFormData(initialPaymentsFormData);
    setPaymentValidation(initialPaymentValidation);
    setOpen(prev => {
      return {
        ...prev,
        addEditPayment: false
      }
    });
  };

  const handleInvoiceCompleteClose = () => {
    setOpen(prev => {
      return {
        ...prev,
        invoiceComplete: false
      }
    });
  }

  const handleSendEmailClose = () => {
    setOpen(prev => {
      return {
        ...prev,
        sendEmail: false
      }
    });
  }

  const handlePayoutAmtClose = () => {
    setOpen(prev => {
      return {
        ...prev,
        payoutAmt: false
      }
    });
    setPayoutAmtItem(null);
    setPayoutAmtData(null);
  };

  const handlePayoutAmtClick = (item: any) => {
    setPayoutAmtItem(item);
    setOpen(prev => {
      return {
        ...prev,
        payoutAmt: true
      }
    });
  };

  const handlePaymentDeleteClose = () => {
    setOpen(prev => {
      return {
        ...prev,
        deletePayment: ''
      }
    });
  }

  const handlePaymentSubmit = useCallback(async (event?: React.FormEvent) => {
    event && event.preventDefault();

    const newValidation = validateAddEditPaymentForm(paymentFormData, allPaymentAccounts);
    setPaymentValidation(newValidation);
    if (!newValidation.validate) return;

    setPaymentLoading(true);

    try {
      const payload = {
        invoice_id: id || "",
        payment_date: paymentFormData.date + 'T00:00:00Z',
        payment_amount: paymentFormData.amount,
        payment_method: paymentFormData.method,
        account_id: paymentFormData.account,
        payment_type: paymentFormData.type,
        status: 'unbilled',
        note: paymentFormData.notes
      };

      if (paymentFormData.id) {
        await PaymentService.edit(paymentFormData.id || "", payload);
      } else {
        await PaymentService.add(payload);
      }

      setPaymentLoading(false);
      handlePaymentAddEditClose();
      setPaymentCounter(prev => prev + 1);
    } catch (error: any) {
      setPaymentLoading(false);
    }
  }, [id, paymentFormData]);

  const handlePaymentDelete = useCallback(async (event?: React.FormEvent) => {
    event && event.preventDefault();
    setPaymentLoading(true);

    try {
      await PaymentService.remove(open.deletePayment);

      setOpen(prev => {
        return {
          ...prev,
          deletePayment: ''
        }
      });
      setPaymentCounter((prev) => prev + 1);
      setPaymentLoading(false);
    } catch (e) {
      setPaymentLoading(false);
    }
  }, [open.deletePayment]);

  const handleSubmit = useCallback(async (action: "1" | "2", event?: React.FormEvent) => {
    event && event.preventDefault();

    const newValidation = validateEditDetailsTabForm(formData);
    setValidation(newValidation);
    if (!newValidation.validate) return;

    if (action === "1") setLoading(true);
    else setActionTwoLoading(true);

    try {
      const payload: EditApi = {
        invoice_date: formData.invoiceDate + 'T00:00:00Z',
        due_date: formData.dueDate + 'T00:00:00Z',
        reservation_ids: formData.selectedReservations,
        transaction_ids: formData.selectedTransactions,
        notes: formData.notes,
        reviewed: formData.reviewed,
        reviewed_by: formData.reviewedBy,
        include_notes: formData.includeNotes,
        is_verified: formData.isVerified,
      };

      let response: any = await InvoiceService.edit(id || "", payload);

      if (response?.success !== false) {
        toast.success(<Toast message={APP_SUCCESS_MESSAGES.invoiceDetailsUpdated} />, { icon: false });

        setShouldBlock(false);
        setTimeout(() => {
          setPaymentCounter(prev => prev + 1);
          if (action === '2') {
            setTab('invoice');
            setPreviewCounter(prev => prev + 1);
          } else {
            setDetailsCounter(prev => prev + 1);
          }
        }, 0);
      }

      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    } catch (e) {
      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    }
  }, [id, formData]);

  const handleMarkAsComplete = useCallback(async (event?: React.FormEvent) => {
    event && event.preventDefault();
    setMarkAsCompleteLoading(true);

    try {
      const response: any = await InvoiceService.complete(id || '');

      if (response?.success !== false) {
        toast.success(<Toast message={APP_SUCCESS_MESSAGES.invoiceCompleted} />, { icon: false });
        setShouldBlock(false);
        setTimeout(() => {
          navigate(`${Modules.invoices}${RouteParamsTypes.view}/${id}`, { replace: true });
          setDetailsCounter(prev => prev + 1);
        }, 0);
      }

      setOpen(prev => {
        return {
          ...prev,
          invoiceComplete: false
        }
      });
      setMarkAsCompleteLoading(false);
    } catch (e) {
      setMarkAsCompleteLoading(false);
    }
  }, [id]);

  const handleSendEmail = useCallback(async (event?: React.FormEvent) => {
    event && event.preventDefault();
    setSendEmailLoading(true);

    try {
      const response: any = await InvoiceService.sendEmail(id || '');

      if (response?.success !== false) {
        toast.success(<Toast message={APP_SUCCESS_MESSAGES.emailSent} />, { icon: false });
        navigate(location.pathname, { replace: true });
        setDetailsCounter(prev => prev + 1);
      }

      setOpen(prev => {
        return {
          ...prev,
          sendEmail: false
        }
      });
      setSendEmailLoading(false);
    } catch (e) {
      setSendEmailLoading(false);
    }
  }, [id, location.pathname, navigate]);

  const handleKeyDown = useCallback((e: any, name?: string) => {
    if (name && [VALIDATION_FIELD_MAPPING.notes].includes(name)) return null
    if (e.key === 'Enter') handleSubmit('1');
  }, [handleSubmit]);

  const handleSelectAll = useCallback((event: any, type: 'reservations' | 'transactions') => {
    setSelectAll(prev => {
      return {
        ...prev,
        [type]: event.target.checked
      }
    })
    setFormData(prev => {
      const tempSelecteds: any[] = [];

      if (event.target.checked) {
        if (type === 'reservations') (reservations || []).map(item => tempSelecteds.push(item.id));
        if (type === 'transactions') (transactions || []).map(item => tempSelecteds.push(item.id));
      }

      return {
        ...prev,
        [type === 'reservations' ? 'selectedReservations' : 'selectedTransactions']: tempSelecteds
      }
    })
  }, [reservations, transactions])

  const handleOpenTransaction = (paramId: string, mode: "view" | "edit") => {
    setTransactionOpen({
      paramId,
      mode
    });
  };

  const handleCloseTransaction = () => {
    setTransactionOpen({
      paramId: '',
      mode: 'view'
    });
    setDetailsCounter(prev => prev + 1);
  };

  const handleOpenReservation = (paramId: string, mode: "view" | "edit") => {
    setReservationOpen({
      paramId,
      mode
    });
  };

  const handleCloseReservation = () => {
    setReservationOpen({
      paramId: '',
      mode: 'view'
    });
    setDetailsCounter(prev => prev + 1);
  };

  useEffect(() => {
    setDropdownSearchData(initialDropdownSearchData)
    setValidation(initialValidation)
  }, [location.key, id]);

  const paymentTableContextValues: TableContextProvider = useMemo(() => {
    return {
      headerSection: <PaymentHeaderSection total={paymentsTotal || 0} kpis={paymentKpis} />,
      headCells: paymentHeadCells, row: PaymentTableRow, order: paymentOrder, orderBy: paymentOrderBy, page: 1, perPage: 100, total: paymentsTotal || 0,
      loading: paymentsLoading, data: payments,
      setPage: () => { },
      setPerPage: () => { },
      setOrder: paymentSetOrder,
      setOrderBy: paymentSetOrderBy,
      rowProps: {
        headCells: paymentHeadCells, view,
        onEditClick: handleEditPaymentClick,
        onDeletePayment: handleDeletePaymentClick
      },
      noDataFoundMessage: t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.noDataMessage'),
      pagination: true
    };
  }, [payments, paymentKpis, paymentsTotal, paymentHeadCells, paymentOrder, paymentOrderBy, paymentsLoading, view, paymentSetOrder, paymentSetOrderBy, handleEditPaymentClick, handleDeletePaymentClick]);

  const reservationTableContextValues: TableContextProvider = useMemo(() => {
    return {
      headerSection: null, row: ReservationRow, data: reservations || [], pagination: false, headCells: reservationHeadCells,
      total: 1000, loading: reservationLoading, noDataFoundMessage: t("invoices.editPreviewPayment.pageTexts.form.detailsTab.reservations.noDataFoundMessage"),
      order: reservationOrder, orderBy: reservationOrderBy, page: 1, perPage: 1000,
      setPage: () => { }, setPerPage: () => { }, setOrder: reservationSetOrder, setOrderBy: reservationSetOrderBy,
      rowProps: {
        formData, view,
        onInputChange: handleInputChange,
        onOpen: handleOpenReservation,
        onPayoutAmtClick: handlePayoutAmtClick
      },
    };
  }, [reservationHeadCells, reservationLoading, reservations, formData, reservationOrder, reservationOrderBy, view, handleInputChange]);

  const transactionTableContextValues: TableContextProvider = useMemo(() => {
    return {
      headerSection: null, row: TransactionRow, data: transactions || [], pagination: false, headCells: transactionHeadCells,
      total: 1000, loading: transactionLoading, noDataFoundMessage: t("invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.noDataFoundMessage"),
      order: transactionOrder, orderBy: transactionOrderBy, page: 1, perPage: 1000,
      setPage: () => { }, setPerPage: () => { }, setOrder: transactionSetOrder, setOrderBy: transactionSetOrderBy,
      rowProps: {
        formData, view,
        onInputChange: handleInputChange,
        onOpen: handleOpenTransaction
      },
    };
  }, [transactionHeadCells, transactionLoading, transactions, formData, transactionOrder, transactionOrderBy, view, transactionSetOrder, transactionSetOrderBy, handleInputChange]);

  const disabledTabs = [...reservations, ...transactions].every(item => (item.status || item.transaction_status) === TRANSACTION_STATUS.unbilled)

  const getInvoiceCompleteMessage = useCallback(() => {
    // Check if any selected reservation has a checkout date greater than today
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const hasFutureCheckout = (reservations || []).some((reservation: any) => {
      if (!formData.selectedReservations.includes(reservation.id)) return false;

      if (reservation.check_out_date_list) {
        const checkoutDate = new Date(reservation.check_out_date_list);
        checkoutDate.setHours(0, 0, 0, 0);
        return checkoutDate > today;
      }
      return false;
    });

    // Check if amount due is not zero
    const amountDueRaw = (paymentKpis as any)?.outstanding_amount;
    const amountDue = parseFloat(amountDueRaw?.replace(/,/g, '') || '0');
    const hasAmountDue = amountDue !== 0;

    // Return the appropriate message based on conditions
    if (hasFutureCheckout && hasAmountDue) {
      return t('invoices.editPreviewPayment.pageTexts.form.invoiceCompleteModal.messageBothConditions');
    } else if (hasFutureCheckout) {
      return t('invoices.editPreviewPayment.pageTexts.form.invoiceCompleteModal.messageFutureCheckout');
    } else if (hasAmountDue) {
      return t('invoices.editPreviewPayment.pageTexts.form.invoiceCompleteModal.messageAmountDue');
    } else {
      return t('invoices.editPreviewPayment.pageTexts.form.invoiceCompleteModal.message');
    }
  }, [reservations, formData.selectedReservations, paymentKpis, t]);

  const invoiceCompleteModalActions = (
    <>
      <Button
        className="btn btn-outline-secondary me-3"
        text={t('invoices.editPreviewPayment.pageTexts.form.invoiceCompleteModal.buttons.cancel')}
        onClick={handleInvoiceCompleteClose}
        disabled={markAsCompleteLoading}
      />
      <Button
        className="btn btn-success"
        text={t('invoices.editPreviewPayment.pageTexts.form.invoiceCompleteModal.buttons.complete')}
        onClick={handleMarkAsComplete}
        disabled={markAsCompleteLoading}
        loading={markAsCompleteLoading}
      />
    </>
  );

  const sendEmailModalActions = (
    <>
      <Button
        className="btn btn-outline-secondary me-3"
        text={t('invoices.editPreviewPayment.pageTexts.form.sendEmailModal.buttons.no')}
        onClick={handleSendEmailClose}
        disabled={sendEmailLoading}
      />
      <Button
        className="btn btn-primary"
        text={t('invoices.editPreviewPayment.pageTexts.form.sendEmailModal.buttons.yes')}
        onClick={handleSendEmail}
        disabled={sendEmailLoading}
        loading={sendEmailLoading}
      />
    </>
  );

  let tabContent;
  if (tab === 'details') {
    tabContent = (
      <>
        <DetailsTab
          tableValues={{
            reservation: reservationTableContextValues,
            transaction: transactionTableContextValues,
          }}
          view={view}
          formData={formData}
          validation={validation}
          selectAll={selectAll}
          dropdownSearchData={dropdownSearchData}
          allReviewedByOptions={allReviewedByOptions}
          reviewedByOptions={reviewedByOptions}
          transactionOpen={transactionOpen}
          reservationOpen={reservationOpen}
          onCloseTransaction={handleCloseTransaction}
          onCloseReservation={handleCloseReservation}
          onSelectAll={handleSelectAll}
          onDropdownSearchChange={handleDropdownSearchInputChange}
          onInputBlur={handleInputBlur}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />

        <hr className="m-0" />

        <DetailsTabButtons
          view={view}
          loading={loading}
          actionTwoLoading={actionTwoLoading}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </>
    );
  } else if (tab === 'invoice') {
    tabContent = (
      <InvoiceTab
        data={pdfData}
        loading={previewLoading}
        onSendEmail={handleSendEmailClick}
      />
    );
  } else if (tab === 'payments') {
    tabContent = (
      <PaymentsTab
        view={view}
        kpis={paymentKpis}
        open={{
          addEdit: open.addEditPayment,
          delete: !!open.deletePayment
        }}
        edit={!!paymentFormData.id}
        loading={paymentLoading}
        tableValues={paymentTableContextValues}
        formData={paymentFormData}
        validation={paymentValidation}
        methods={paymentMethods}
        allMethods={allPaymentMethods}
        accounts={paymentAccounts}
        allAccounts={allPaymentAccounts}
        dropdownSearchData={dropdownSearchData}
        onInputChange={handlePaymentInputChange}
        onInputBlur={handlePaymentInputBlur}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onAddClick={handleAddPaymentClick}
        onDelete={handlePaymentDelete}
        onSubmit={handlePaymentSubmit}
        paymentTypeOptions={invoicePaymentTypeOptions}
        onDeleteClose={handlePaymentDeleteClose}
        onAddEditClose={handlePaymentAddEditClose}
      />
    );
  }

  return (
    <>
      <Header view={view} loading={getContentLoading} onComplete={handleMarkAsCompleteClick} />

      <PrimaryDetails
        loading={getContentLoading}
        formData={formData}
      />

      <Cards formData={formData} />

      <div className="card mb-6">
        <Tabs tab={tab} disabled={false} onTabChange={handleTabChange} />

        <hr className="m-0"></hr>

        {tabContent}
      </div>

      <Modal
        open={!!open.invoiceComplete}
        onClose={handleInvoiceCompleteClose}
        title={t('invoices.editPreviewPayment.pageTexts.form.invoiceCompleteModal.title')}
        content={getInvoiceCompleteMessage()}
        action={invoiceCompleteModalActions}
        contentClassName="text-center"
        maxWidth="sm"
      />

      <Modal
        open={!!open.sendEmail}
        onClose={handleSendEmailClose}
        title={t('invoices.editPreviewPayment.pageTexts.form.sendEmailModal.title')}
        content={t('invoices.editPreviewPayment.pageTexts.form.sendEmailModal.message')}
        action={sendEmailModalActions}
        contentClassName="text-center"
        maxWidth="sm"
      />

      <Modal
        open={!!open.payoutAmt}
        onClose={handlePayoutAmtClose}
        maxWidth="md"
        titleClassName="text-start"
        actionClassName="justify-content-start"
        title={t('reconciliation.payout.list.pageTexts.modals.payoutAmt.title')}
        content={<PayoutAmtModalContent item={payoutAmtData} loading={payoutAmtLoading} />}
        action={
          <Button
            className="btn btn-outline-secondary"
            text={t('reconciliation.payout.list.pageTexts.modals.payoutAmt.buttons.close')}
            onClick={handlePayoutAmtClose}
          />
        }
      />

      <ConfirmationFormRouting
        open={showPrompt}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
      />
    </>
  );
};

export default EditPreviewPayment;